import { createContext, useContext, useState, useEffect } from 'react'
import { api, setTokens, clearTokens, setDrillInHotel, clearDrillInHotel } from '../utils/api'

const AuthContext = createContext()

const CORPORATE_ROLES = ['corporate_admin', 'vp', 'regional_director']
const CORPORATE_DRILL_IN_PAGES = ['dashboard', 'reports', 'group_resume']

const ROLE_ACCESS = {
  admin: ['dashboard','leads','contacts','deals','events','calendar','reports','space','group_resume','proposals','contracts','team','profile','settings','catering','function_diary'],
  general_manager: ['dashboard','leads','contacts','deals','events','calendar','reports','space','group_resume','proposals','contracts','team','profile','settings','catering','function_diary'],
  dos: ['dashboard','leads','contacts','deals','events','calendar','reports','space','group_resume','proposals','contracts','team','profile','settings','catering','function_diary'],
  sales_manager: ['dashboard','leads','contacts','deals','events','calendar','reports','space','group_resume','proposals','contracts','profile','settings','catering','function_diary'],
  sales_coordinator: ['dashboard','leads','contacts','events','calendar','group_resume','proposals','profile','catering','function_diary'],
  front_desk: ['dashboard','group_resume','calendar','profile'],
}

const FINANCIAL_ROLES = ['admin', 'general_manager', 'dos', 'sales_manager', 'corporate_admin', 'vp', 'regional_director']

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem('leadiox-user')) } catch { return null }
  })
  const [hotel, setHotel] = useState(null)
  const [managementCompany, setManagementCompany] = useState(null)
  const [drillInHotel, setDrillInHotelState] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const token = localStorage.getItem('leadiox-access-token')
    if (token && user) {
      api('/auth/me')
        .then(data => {
          setUser(data.user)
          setHotel(data.hotel)
          setManagementCompany(data.managementCompany || null)
          try { localStorage.setItem('leadiox-user', JSON.stringify(data.user)) } catch {}
        })
        .catch(() => { clearTokens(); setUser(null) })
        .finally(() => setLoading(false))
    } else {
      setLoading(false)
    }
  }, [])

  const login = async (email, password) => {
    const data = await api('/auth/login', { method: 'POST', body: { email, password } })
    setTokens(data.accessToken, data.refreshToken)
    setUser(data.user)
    setHotel(data.hotel)
    setManagementCompany(data.managementCompany || null)
    try { localStorage.setItem('leadiox-user', JSON.stringify(data.user)) } catch {}
    return data
  }

  const logout = async () => {
    try { await api('/auth/logout', { method: 'POST' }) } catch {}
    clearTokens()
    clearDrillInHotel()
    setUser(null)
    setHotel(null)
    setManagementCompany(null)
    setDrillInHotelState(null)
  }

  const isCorporate = () => user && CORPORATE_ROLES.includes(user.role)

  const hasAccess = (page) => {
    if (!user) return false
    // Corporate user in drill-in mode: only show drill-in pages
    if (isCorporate() && drillInHotel) return CORPORATE_DRILL_IN_PAGES.includes(page)
    // Corporate user not drilling in: no hotel pages
    if (isCorporate()) return false
    return (ROLE_ACCESS[user.role] || []).includes(page)
  }

  const canSeeFinancials = () => user && FINANCIAL_ROLES.includes(user.role)

  const isReadOnly = () => {
    if (!user) return false
    if (isCorporate() && drillInHotel) return true
    return user.role === 'front_desk'
  }

  const isPlatformAdmin = () => user?.is_platform_admin === true

  // Corporate drill-in functions
  const enterHotel = (hotelData) => {
    setDrillInHotelState(hotelData)
    setDrillInHotel(hotelData.id)
  }

  const exitHotel = () => {
    setDrillInHotelState(null)
    clearDrillInHotel()
  }

  return (
    <AuthContext.Provider value={{
      user, hotel, managementCompany, drillInHotel, loading,
      login, logout, hasAccess, canSeeFinancials, isReadOnly, isPlatformAdmin,
      isCorporate, enterHotel, exitHotel,
    }}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => useContext(AuthContext)
